import React from 'react';
import { FiX } from 'react-icons/fi';
import Icon from '../common/Icon';
import { useOrderFormModal } from '../../hooks/useOrderFormModal';

type OrderFormModalProps = Pick<
  ReturnType<typeof useOrderFormModal>,
  | 'showModal'
  | 'editingOrder'
  | 'formData'
  | 'setFormData'
  | 'customerSearch'
  | 'setCustomerSearch'
  | 'customerDropdownOpen'
  | 'setCustomerDropdownOpen'
  | 'customerDropdownRef'
  | 'modalError'
  | 'users'
  | 'brands'
  | 'suppliers'
  | 'customers'
  | 'closeModal'
  | 'handleSubmit'
>;

const OrderFormModal: React.FC<OrderFormModalProps> = ({
  showModal,
  editingOrder,
  formData,
  setFormData,
  customerSearch,
  setCustomerSearch,
  customerDropdownOpen,
  setCustomerDropdownOpen,
  customerDropdownRef,
  modalError,
  users,
  brands,
  suppliers,
  customers,
  closeModal,
  handleSubmit,
}) => {
  if (!showModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
      <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-4xl shadow-xl rounded-lg bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-bold text-gray-900">
            {editingOrder ? 'Edit Order' : 'Add Order'}
          </h3>
          <button
            type="button"
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 p-0.5"
            aria-label="Close"
          >
            <Icon component={FiX} className="w-5 h-5" />
          </button>
        </div>

        {modalError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {modalError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div ref={customerDropdownRef} className="relative">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
              <input type="hidden" value={formData.customer_id || ''} required readOnly />
              <button
                type="button"
                onClick={() => setCustomerDropdownOpen(!customerDropdownOpen)}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm text-left bg-white flex items-center justify-between"
              >
                <span className={formData.customer_id ? 'text-gray-900' : 'text-gray-500'}>
                  {formData.customer_id
                    ? (() => {
                        const c = customers.find((cust) => cust.id === formData.customer_id);
                        return c ? `${c.party_name}${c.customer_code ? ` (${c.customer_code})` : ''}` : 'Select Customer';
                      })()
                    : 'Select Customer'}
                </span>
                <span className="text-gray-400 ml-1">{customerDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {customerDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 flex flex-col">
                  <input
                    type="text"
                    placeholder="Search by name or code..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="px-2 py-1.5 text-sm border-b border-gray-200 rounded-t-md focus:outline-none focus:ring-1 focus:ring-yellow-500"
                    autoFocus
                  />
                  <ul className="overflow-y-auto flex-1 py-1">
                    {customers
                      .filter((c) => {
                        const q = customerSearch.trim().toLowerCase();
                        if (!q) return true;
                        const name = (c.party_name || '').toLowerCase();
                        const code = (c.customer_code || '').toLowerCase();
                        return name.includes(q) || code.includes(q);
                      })
                      .map((customer) => (
                        <li key={customer.id}>
                          <button
                            type="button"
                            onClick={() => {
                              const locs = customer.locations ?? [];
                              const oneLoc = locs.length === 1 ? locs[0] : undefined;
                              setFormData({
                                ...formData,
                                customer_id: customer.id,
                                customer_code: customer.customer_code,
                                brand_id: customer.brand_id ?? 0,
                                supplier_id: customer.supplier_id,
                                sales_officer_id: customer.sales_officer_id ?? formData.sales_officer_id,
                                location_id: oneLoc?.id,
                                district: oneLoc?.project_district ?? '',
                                destination: oneLoc?.project_location ?? '',
                              });
                              setCustomerDropdownOpen(false);
                              setCustomerSearch('');
                            }}
                            className={`w-full px-2 py-1.5 text-left text-sm hover:bg-yellow-50 ${formData.customer_id === customer.id ? 'bg-yellow-100' : ''}`}
                          >
                            {customer.party_name} {customer.customer_code && `(${customer.customer_code})`}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Location *</label>
              <select
                required
                value={formData.location_id ?? ''}
                onChange={(e) => {
                  const locId = e.target.value ? parseInt(e.target.value, 10) : undefined;
                  const customer = customers.find((c) => c.id === formData.customer_id);
                  const loc = (customer?.locations ?? []).find((l) => l.id === locId);
                  setFormData({
                    ...formData,
                    location_id: locId,
                    district: loc?.project_district ?? '',
                    destination: loc?.project_location ?? '',
                  });
                }}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                disabled={!formData.customer_id}
              >
                <option value="">{formData.customer_id ? 'Select Location' : 'Select Customer first'}</option>
                {(customers.find((c) => c.id === formData.customer_id)?.locations ?? []).map((loc, idx) => (
                  <option key={loc.id ?? idx} value={loc.id ?? ''}>
                    {[loc.project_district, loc.project_location].filter(Boolean).join(', ') || `Location ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                value={formData.brand_id || ''}
                disabled
                className="block w-full border border-gray-200 bg-gray-50 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm text-gray-700"
              >
                <option value="">—</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Supplier</label>
              <select
                value={formData.supplier_id || ''}
                disabled
                className="block w-full border border-gray-200 bg-gray-50 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm text-gray-700"
              >
                <option value="">—</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Order Date *</label>
              <input
                type="date"
                required
                value={formData.order_date}
                onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Order Number</label>
              <input
                type="text"
                value={formData.order_number || ''}
                onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                placeholder="Auto-generated if empty"
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Sales Officer *</label>
              <select
                required
                value={formData.sales_officer_id || ''}
                onChange={(e) => setFormData({ ...formData, sales_officer_id: parseInt(e.target.value) })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">Select Sales Officer</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Order Type</label>
              <select
                value={formData.order_type}
                onChange={(e) => setFormData({ ...formData, order_type: e.target.value as 'EX' | 'FOR' })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="FOR">FOR</option>
                <option value="EX">EX</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Product Type</label>
              <input
                type="text"
                value={formData.product_type || ''}
                onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                placeholder="e.g., PPC, PSC, PCC, OPC 43, OPC 53, SRPC"
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity || ''}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Price per Bag</label>
              <input
                type="number"
                step="0.01"
                value={formData.price_per_bag || ''}
                onChange={(e) => setFormData({ ...formData, price_per_bag: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={formData.payment_status || ''}
                onChange={(e) => setFormData({ ...formData, payment_status: e.target.value || undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">PO Status</label>
              <select
                value={formData.po_status || ''}
                onChange={(e) => setFormData({ ...formData, po_status: e.target.value || undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Dispatch Status</label>
              <select
                value={formData.dispatch_status || ''}
                onChange={(e) => setFormData({ ...formData, dispatch_status: e.target.value || undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Invoice Status</label>
              <select
                value={formData.invoice_status || ''}
                onChange={(e) => setFormData({ ...formData, invoice_status: e.target.value || undefined })}
                className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="in_progress">In Progress</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
            <button
              type="button"
              onClick={closeModal}
              className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
            >
              {editingOrder ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderFormModal;
